import { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Nav, Table, Form, Button, Alert } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useParams, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getStudent, addObservation, addIncident } from '../../../actions/studentActions'
import Loader from '../../../components/loaders/Loader'
import { toastSuccess } from '../../../components/swal/toast'

export default function StudentCard() {
    const { t } = useTranslation()
    const { studentId } = useParams()
    const dispatch = useDispatch()
    const { currentStudent, loading, studentError } = useSelector(state => state.students)
    const [activeTab, setActiveTab] = useState('info')
    const [observation, setObservation] = useState('')
    const [incidentType, setIncidentType] = useState('minor')
    const [incidentDesc, setIncidentDesc] = useState('')

    const student = currentStudent?.student
    const evaluations = currentStudent?.evaluations || []
    const messages = currentStudent?.messages || []
    const attendanceHistory = currentStudent?.attendanceHistory || []
    const studentTasks = currentStudent?.tasks || []
    const observationHistory = currentStudent?.observations || []
    const incidentHistory = currentStudent?.incidents || []

    const competenceKeys = ['speaking', 'listening', 'reading', 'writing', 'useOfEnglish']
    const competenceStats = competenceKeys.map(key => {
        const values = evaluations
            .map(ev => ev.competences?.[key])
            .filter(v => v !== null && v !== undefined)
        const avg = values.length > 0 ? values.reduce((a, b) => a + Number(b), 0) / values.length : null
        return { key, avg, count: values.length }
    })

    const statusBadge = (status) => {
        const map = { present: ['success', t('teacher.classes.present')], absent: ['danger', t('teacher.classes.absent')], late: ['warning', t('teacher.classes.late')], justified: ['info', t('teacher.classes.justified')] }
        const [variant, label] = map[status] || ['secondary', status]
        return <span className={`badge bg-${variant} rounded-pill`}>{label}</span>
    }

    useEffect(() => {
        if (studentId) dispatch(getStudent(studentId))
    }, [dispatch, studentId])

    if (loading && !student) return <Loader />

    if (studentError) return (
        <Container className='my-3 p-5'>
            <Alert variant='danger'>{studentError}</Alert>
        </Container>
    )

    const typeLabel = (type) => {
        switch (type) {
            case 'continuous': return t('teacher.evaluations.continuous')
            case 'competence': return t('teacher.evaluations.competence')
            case 'exam': return t('teacher.evaluations.exam')
            case 'mock_exam': return t('teacher.evaluations.mockexam')
            default: return type
        }
    }

    const handleSaveObservation = () => {
        if (!observation.trim()) return
        dispatch(addObservation(studentId, observation))
        toastSuccess(t('teacher.students.observationSaved'))
        setObservation('')
    }

    const handleSaveIncident = () => {
        if (!incidentDesc.trim()) return
        dispatch(addIncident(studentId, incidentType, incidentDesc))
        toastSuccess(t('teacher.students.incidentSaved'))
        setIncidentDesc('')
    }

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col>
                    <Link to='/teacher/students' className='text-decoration-none mb-2 d-inline-block'>{t('global.return')}</Link>
                    <h3 className='fw-bold mb-1'>{student?.name || 'Alumno'}</h3>
                    <p className='text-muted mb-4'>{student?.email || ''}</p>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card className='border-0 shadow-sm rounded-4'>
                        <Card.Body className='p-0'>
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className='px-4 pt-3'>
                                <Nav.Item><Nav.Link eventKey="info">Info</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="evaluations">{t('teacher.evaluations.title')}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="progress">{t('teacher.students.progress')}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="attendance">{t('teacher.students.attendance')}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="tasks">{t('teacher.students.tasks')}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="observations">{t('teacher.classes.observations')}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="incidents">{t('teacher.classes.incidents')}</Nav.Link></Nav.Item>
                                <Nav.Item><Nav.Link eventKey="messages">{t('teacher.sidebar.communications')}</Nav.Link></Nav.Item>
                            </Nav>
                            <div className='p-4'>
                                {activeTab === 'info' && (
                                    <div>
                                        <h5 className='fw-bold mb-3'>{t('teacher.students.studentdata')}</h5>
                                        <Row>
                                            <Col md={6}>
                                                <p><strong>{t('teacher.students.studentname')}</strong> {student?.name}</p>
                                                <p><strong>{t('teacher.students.studentemail')}</strong> {student?.email}</p>
                                                <p><strong>{t('teacher.students.studentphone')}</strong> {student?.phone || '-'}</p>
                                            </Col>
                                            <Col md={6}>
                                                <h6 className='fw-bold mt-3'>{t('teacher.students.enrollments')}</h6>
                                                {student?.enrollments?.length > 0
                                                    ? student.enrollments.map((e, idx) => (
                                                        <div key={idx} className='p-2 mb-2 bg-light rounded-3'>
                                                            <p className='mb-0 fw-semibold'>{e.groupName || t('teacher.students.nogroup')}</p>
                                                            <p className='mb-0' style={{ fontSize: '13px' }}>{e.courseName}</p>
                                                        </div>
                                                    ))
                                                    : <p className='text-muted'>{t('teacher.students.noenrollments')}</p>
                                                }
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                                {activeTab === 'evaluations' && (
                                    <div>
                                        <h5 className='fw-bold mb-3'>{t('teacher.evaluations.title')}</h5>
                                        {evaluations.length > 0
                                            ? <Table hover responsive>
                                                <thead>
                                                    <tr>
                                                        <th>{t('teacher.students.evaltype')}</th>
                                                        <th>{t('teacher.students.evalscore')}</th>
                                                        <th>{t('teacher.students.evalperiod')}</th>
                                                        <th>{t('teacher.students.evaldate')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {evaluations.map(ev => (
                                                        <tr key={ev._id}>
                                                            <td><span className='badge bg-light text-dark border'>{typeLabel(ev.type)}</span></td>
                                                            <td>{ev.score}/{ev.maxScore}</td>
                                                            <td>{ev.period || '-'}</td>
                                                            <td>{ev.date ? new Date(ev.date).toLocaleDateString() : '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                            : <p className='text-muted'>{t('teacher.students.noevaluations')}</p>
                                        }
                                    </div>
                                )}
                                {activeTab === 'progress' && (
                                    <div>
                                        <h5 className='fw-bold mb-3'>{t('teacher.students.progress')}</h5>
                                        <Row className='g-2'>
                                            {competenceStats.map(c => (
                                                <Col key={c.key} xs={12} md={6} xl={4}>
                                                    <div className='p-3 bg-light rounded-3 h-100'>
                                                        <div className='d-flex justify-content-between align-items-center mb-2'>
                                                            <span className='fw-semibold' style={{ fontSize: '14px' }}>{t(`teacher.students.${c.key === 'useOfEnglish' ? 'useofenglish' : c.key}`)}</span>
                                                            <span className='fw-bold' style={{ fontSize: '14px' }}>{c.avg !== null ? `${c.avg.toFixed(1)}` : '--'}</span>
                                                        </div>
                                                        <div className='rounded-pill' style={{ height: '8px', backgroundColor: '#e5e7eb' }}>
                                                            <div className='rounded-pill bg-primary' style={{ height: '8px', width: `${c.avg !== null ? Math.min(100, (c.avg / 10) * 100) : 0}%` }} />
                                                        </div>
                                                        <p className='text-muted mb-0 mt-1' style={{ fontSize: '11px' }}>{t('teacher.students.average')} · {c.count} {t('teacher.evaluations.title').toLowerCase()}</p>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}
                                {activeTab === 'attendance' && (
                                    <div>
                                        <h5 className='fw-bold mb-3'>{t('teacher.students.attendance')} — {attendanceHistory.length}</h5>
                                        {attendanceHistory.length > 0
                                            ? <Table hover responsive>
                                                <thead>
                                                    <tr>
                                                        <th>{t('global.date')}</th>
                                                        <th>{t('global.group')}</th>
                                                        <th>{t('teacher.tasks.status')}</th>
                                                        <th>{t('teacher.classes.observations')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {attendanceHistory.map((a, idx) => (
                                                        <tr key={idx}>
                                                            <td>{a.date ? new Date(a.date).toLocaleDateString() : '-'}</td>
                                                            <td>{a.groupName || '-'}</td>
                                                            <td>{statusBadge(a.status)}</td>
                                                            <td className='text-muted' style={{ fontSize: '13px' }}>{a.observation || '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                            : <p className='text-muted'>{t('teacher.students.noattendance')}</p>
                                        }
                                    </div>
                                )}
                                {activeTab === 'tasks' && (
                                    <div>
                                        <h5 className='fw-bold mb-3'>{t('teacher.students.tasks')} — {studentTasks.length}</h5>
                                        {studentTasks.length > 0
                                            ? studentTasks.map(task => (
                                                <div key={task._id} className='d-flex align-items-start justify-content-between p-3 mb-2 bg-light rounded-3'>
                                                    <div>
                                                        <p className='fw-semibold mb-0' style={{ fontSize: '14px' }}>{task.title}</p>
                                                        <p className='text-muted mb-0' style={{ fontSize: '12px' }}>
                                                            {task.groupName || ''}
                                                            {task.dueDate ? ` · ${new Date(task.dueDate).toLocaleDateString()}` : ''}
                                                        </p>
                                                        {task.comments && <p className='mb-0 mt-1' style={{ fontSize: '13px' }}>{task.comments}</p>}
                                                    </div>
                                                    {task.grade !== null && task.grade !== undefined
                                                        ? <span className='badge bg-success rounded-pill flex-shrink-0 ms-2'>{task.grade}/10</span>
                                                        : <span className='badge bg-warning rounded-pill flex-shrink-0 ms-2'>{t('teacher.tasks.pending')}</span>
                                                    }
                                                </div>
                                            ))
                                            : <p className='text-muted'>{t('teacher.students.notasks')}</p>
                                        }
                                    </div>
                                )}
                                {activeTab === 'observations' && (
                                    <div>
                                        <h5 className='fw-bold mb-3'>{t('teacher.students.newobservation')}</h5>
                                        <Form.Group className='mb-3'>
                                            <Form.Control as='textarea' rows={3} placeholder={t('teacher.students.observationplaceholder')}
                                                style={{ borderRadius: '10px' }}
                                                value={observation} onChange={e => setObservation(e.target.value)} />
                                        </Form.Group>
                                        <Button variant='primary' className='rounded-3' onClick={handleSaveObservation}>
                                            {t('teacher.students.saveobservation')}
                                        </Button>
                                        <h6 className='fw-bold mt-4 mb-2'>{t('teacher.students.history')}</h6>
                                        {observationHistory.length > 0
                                            ? observationHistory.map((o, idx) => (
                                                <div key={idx} className='p-2 mb-1 bg-light rounded-3'>
                                                    <p className='mb-0' style={{ fontSize: '13px' }}>{o.text}</p>
                                                    <p className='text-muted mb-0' style={{ fontSize: '11px' }}>
                                                        {o.date ? new Date(o.date).toLocaleDateString() : ''}{o.groupName ? ` · ${o.groupName}` : ''}
                                                    </p>
                                                </div>
                                            ))
                                            : <p className='text-muted' style={{ fontSize: '13px' }}>{t('teacher.students.noobservations')}</p>
                                        }
                                    </div>
                                )}
                                {activeTab === 'incidents' && (
                                    <div>
                                        <h5 className='fw-bold mb-3'>{t('teacher.students.newincident')}</h5>
                                        <Form.Group className='mb-3'>
                                            <Form.Label>{t('teacher.students.incidenttype')}</Form.Label>
                                            <Form.Select style={{ borderRadius: '10px' }}
                                                value={incidentType} onChange={e => setIncidentType(e.target.value)}>
                                                <option value='minor'>{t('teacher.students.incidentminor')}</option>
                                                <option value='major'>{t('teacher.students.incidentmajor')}</option>
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className='mb-3'>
                                            <Form.Control as='textarea' rows={3} placeholder={t('teacher.students.incidentplaceholder')}
                                                style={{ borderRadius: '10px' }}
                                                value={incidentDesc} onChange={e => setIncidentDesc(e.target.value)} />
                                        </Form.Group>
                                        <Button variant='primary' className='rounded-3' onClick={handleSaveIncident}>
                                            {t('teacher.students.saveincident')}
                                        </Button>
                                        <h6 className='fw-bold mt-4 mb-2'>{t('teacher.students.history')}</h6>
                                        {incidentHistory.length > 0
                                            ? incidentHistory.map((inc, idx) => (
                                                <div key={idx} className='p-2 mb-1 bg-light rounded-3'>
                                                    <span className={`badge rounded-pill ${inc.type === 'major' ? 'bg-danger' : 'bg-warning'} me-2`}>
                                                        {inc.type === 'major' ? t('teacher.students.incidentmajor') : t('teacher.students.incidentminor')}
                                                    </span>
                                                    <span style={{ fontSize: '13px' }}>{inc.description}</span>
                                                    <p className='text-muted mb-0' style={{ fontSize: '11px' }}>
                                                        {inc.date ? new Date(inc.date).toLocaleDateString() : ''}{inc.groupName ? ` · ${inc.groupName}` : ''}
                                                    </p>
                                                </div>
                                            ))
                                            : <p className='text-muted' style={{ fontSize: '13px' }}>{t('teacher.students.noincidents')}</p>
                                        }
                                    </div>
                                )}
                                {activeTab === 'messages' && (
                                    <div>
                                        <h5 className='fw-bold mb-3'>{t('teacher.communicationhistory')}</h5>
                                        {messages.length > 0
                                            ? messages.map(msg => (
                                                <div key={msg._id} className='p-3 mb-2 bg-light rounded-3'>
                                                    <div className='d-flex justify-content-between align-items-start mb-1'>
                                                        <p className='fw-semibold mb-0' style={{ fontSize: '14px' }}>{msg.subject}</p>
                                                        <span className='text-muted' style={{ fontSize: '11px' }}>
                                                            {new Date(msg.createdAt).toLocaleDateString('es-ES')}
                                                        </span>
                                                    </div>
                                                    <p className='mb-1' style={{ fontSize: '13px', color: '#374151' }}>{msg.message}</p>
                                                    <p className='text-muted mb-0' style={{ fontSize: '11px' }}>
                                                        {msg.senderName ? `${t('global.from')}: ${msg.senderName}` : ''} → {msg.recipientName || msg.groupName || ''}
                                                    </p>
                                                </div>
                                            ))
                                            : <p className='text-muted'>{t('teacher.nomessages')}</p>
                                        }
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}
